/**
 * Blok konten — satuan isi yang dipakai bersama oleh Panduan dan Penyakit.
 *
 * Sebelumnya seksi penyakit (gejala, penyebab, pencegahan) hanya `string[]`,
 * sehingga admin tidak bisa membuat sub-judul, tabel, atau kotak sorotan.
 * Sekarang tiap seksi adalah daftar blok. Bentuk lama tetap terbaca: `string[]`
 * dinormalkan jadi satu blok `list` — lihat `normalisasiSeksi()`.
 *
 * Tidak ada HTML mentah di sini. Penekanan ditulis dengan penanda ringan
 * (`**tebal**`, `*miring*`) dan dirender jadi elemen React oleh <TeksKaya>,
 * jadi tidak ada jalur `dangerouslySetInnerHTML` yang perlu disanitasi.
 */

export type JenisBlok =
  | "text"        // paragraf
  | "subheading"  // sub-judul di dalam seksi, mis. "Bentuk akut (mendadak)"
  | "list"        // poin-poin
  | "tip"         // kotak sorotan positif
  | "warning"     // kotak sorotan peringatan
  | "table"       // tabel kolom + baris
  | "image"
  | "video"

export interface BlokKonten {
  type: JenisBlok
  /** isi untuk text / subheading / tip / warning */
  content?: string
  /** isi untuk list */
  items?: string[]
  /** judul kotak sorotan (tip/warning); kosong → pakai judul bawaan */
  title?: string
  /** image / video */
  src?: string
  caption?: string
  alt?: string
  /** table */
  columns?: string[]
  rows?: string[][]
}

/**
 * Nilai seksi apa adanya dari JSON: bentuk lama (`string[]`) atau bentuk baru
 * (`BlokKonten[]`). Selalu lewatkan ke `normalisasiSeksi()` sebelum dipakai.
 */
export type SeksiMentah = string[] | BlokKonten[]

const JENIS_VALID: ReadonlySet<string> = new Set<JenisBlok>([
  "text", "subheading", "list", "tip", "warning", "table", "image", "video",
])

function isBlok(x: unknown): x is BlokKonten {
  return !!x && typeof x === "object" && JENIS_VALID.has((x as BlokKonten).type)
}

/**
 * Seragamkan nilai seksi menjadi daftar blok.
 *
 * - `string[]` (bentuk lama) → satu blok `list`
 * - campuran string & blok → string berurutan digabung jadi satu blok `list`
 * - entri yang tidak dikenali dibuang
 *
 * HANYA menyeragamkan bentuk — blok kosong TIDAK dibuang, karena editor admin
 * memanggil ini tiap render dan blok yang baru ditambahkan memang masih kosong.
 * Penyaringan untuk pembaca dilakukan `blokTerisi()`.
 */
export function normalisasiSeksi(nilai: unknown): BlokKonten[] {
  if (!Array.isArray(nilai)) return []

  const hasil: BlokKonten[] = []
  let bufferTeks: string[] | null = null

  const tutupBuffer = () => {
    if (bufferTeks) {
      hasil.push({ type: "list", items: bufferTeks })
      bufferTeks = null
    }
  }

  for (const entri of nilai) {
    if (typeof entri === "string") {
      if (!bufferTeks) bufferTeks = []
      bufferTeks.push(entri)
      continue
    }
    if (isBlok(entri)) {
      tutupBuffer()
      hasil.push(entri)
    }
    // selain itu: diabaikan
  }
  tutupBuffer()

  return hasil
}

/** Buang sel/poin kosong di dalam satu blok. */
function rapikan(b: BlokKonten): BlokKonten {
  switch (b.type) {
    case "list":
      return { ...b, items: (b.items ?? []).filter((i) => i.trim() !== "") }
    case "table": {
      const columns = (b.columns ?? []).map((c) => c ?? "")
      const rows = (b.rows ?? [])
        .map((r) => Array.from({ length: columns.length }, (_, i) => r?.[i] ?? ""))
        .filter((r) => r.some((c) => c.trim() !== ""))
      return { ...b, columns, rows }
    }
    default:
      return b
  }
}

/** Blok yang layak ditampilkan ke pembaca — yang masih kosong dibuang. */
export function blokTerisi(nilai: unknown): BlokKonten[] {
  return normalisasiSeksi(nilai)
    .map(rapikan)
    .filter((b) => {
      switch (b.type) {
        case "list":
          return (b.items?.length ?? 0) > 0
        case "table":
          return (b.columns?.length ?? 0) > 0 && (b.rows?.length ?? 0) > 0
        case "image":
        case "video":
          return (b.src ?? "").trim() !== ""
        default:
          return (b.content ?? "").trim() !== ""
      }
    })
}

/** Hilangkan penanda penekanan supaya teks aman dipakai di meta/pencarian. */
export function teksPolos(s: string): string {
  return s.replace(/\*{1,2}/g, "")
}

/**
 * Ratakan seksi jadi daftar teks biasa.
 *
 * Dipakai bagian yang butuh string murni, bukan tampilan: pencarian di halaman
 * daftar penyakit, cuplikan gejala di beranda, dan deskripsi SEO. Tanpa ini,
 * seksi yang sudah dipindah ke blok akan hilang dari pencarian.
 */
export function teksDatar(nilai: unknown): string[] {
  const keluar: string[] = []
  const tambah = (s: string | undefined) => {
    const t = teksPolos(s ?? "").trim()
    if (t) keluar.push(t)
  }

  for (const b of blokTerisi(nilai)) {
    switch (b.type) {
      case "list":
        (b.items ?? []).forEach(tambah)
        break
      case "table":
        (b.columns ?? []).forEach(tambah)
        ;(b.rows ?? []).forEach((r) => r.forEach(tambah))
        break
      case "image":
      case "video":
        tambah(b.caption)
        break
      default:
        tambah(b.title)
        tambah(b.content)
    }
  }
  return keluar
}

/** Versi satu string — untuk pencarian sederhana. */
export function teksDatarGabung(nilai: unknown, pemisah = " "): string {
  return teksDatar(nilai).join(pemisah)
}
